import { useCallback, useMemo } from 'react';

import { useAuth } from 'context/AuthContext';
import { GetVersion } from 'services/utils';

/** O que uma tela precisa de um ciclo do seu recorte. */
export interface CicloDoRecorte {
  _id: string;
  title: string;
  date_start: number;
}

/**
 * O ciclo de uma tela com seletor próprio (CIC-10 a CIC-12, CIC-15, CIC-16): é o ciclo do app, quando ele está no
 * recorte da tela. Sem estado próprio, o seletor da tela e o do menu nunca divergem: trocar em um troca no outro.
 *
 * - `ciclo`: o ciclo do app, se a tela o tem;
 * - `foraDaTela`: o ciclo do app, quando a tela não o tem. Decide o aviso de CIC-12 e também o recuo de
 *   `cicloExibido`: quem remover o aviso de uma tela não pode tirar este valor do hook;
 * - `maisRecente`: o ciclo mais recente do recorte (o botão do aviso);
 * - `cicloExibido`: o ciclo que as telas sem aviso mostram (Catálogo de Experiências, Destaques e Extrato de
 *   premiação, CIC-15 e CIC-16): o do app, ou, fora do recorte, o mais recente. Com o ciclo do app ainda indefinido,
 *   nenhum: a tela espera;
 * - `trocar(id)`: grava o ciclo do app, com a versão, como o seletor do menu.
 */
export function useCicloDaTela<T extends CicloDoRecorte>(ciclos: T[]) {
  const { cycleSelected, setCycleSelected } = useAuth();

  const ciclo = ciclos.find(item => item._id === cycleSelected?._id);
  const foraDaTela = cycleSelected && ciclos.length > 0 && !ciclo ? cycleSelected : undefined;
  const maisRecente = useMemo(
    () =>
      ciclos.reduce<T | undefined>(
        (recente, item) => (!recente || item.date_start > recente.date_start ? item : recente),
        undefined,
      ),
    [ciclos],
  );

  const trocar = useCallback(
    (cicloId: string) => {
      const escolhido = ciclos.find(item => item._id === cicloId);
      if (escolhido) setCycleSelected({ _id: escolhido._id, title: escolhido.title, version: GetVersion(escolhido.date_start) });
    },
    [ciclos, setCycleSelected],
  );

  const cicloExibido = ciclo ?? (foraDaTela ? maisRecente : undefined);

  return { ciclo, foraDaTela, maisRecente, cicloExibido, trocar };
}
